import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { fetchSkiAreas } from '../services/skiAreasService';
import {
  startImport, integrateImport, fetchVersions, promoteVersion, fetchLock,
} from '../services/mapManagementService';
import MapImportPreview from '../components/MapImportPreview';
import { useError } from '../contexts/ErrorContext';
import './MapImportPage.css';

const TABS = [
  { id: 'runs', label: 'Runs' },
  { id: 'lifts', label: 'Lifts' },
  { id: 'pois', label: 'POIs' },
];

const MapImportPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();

  const [skiAreas, setSkiAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [mapboxUrl, setMapboxUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('runs');
  const [lock, setLock] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [notes, setNotes] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [staleBase, setStaleBase] = useState(null);
  const [versions, setVersions] = useState(null);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchSkiAreas()
      .then(areas => { if (!cancelled) setSkiAreas(areas || []); })
      .catch(err => { if (!cancelled) { setError(err.message); addError(err.message, err.stack, 'Map Import'); } })
      .finally(() => { if (!cancelled) setLoadingAreas(false); });
    return () => { cancelled = true; };
  }, [addError]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return skiAreas
      .filter(a => (a.name || '').toLowerCase().includes(q))
      .slice(0, 25);
  }, [skiAreas, query]);

  const handlePull = useCallback(async () => {
    if (!selected || !mapboxUrl.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await startImport(selected.id, mapboxUrl.trim());
      setResult(data);
      setTab('runs');
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Map Import');
    } finally {
      setBusy(false);
    }
  }, [selected, mapboxUrl, addError]);

  const handleReset = useCallback(() => {
    setResult(null);
    setError(null);
    setMapboxUrl('');
    setPublished(null);
    setStaleBase(null);
  }, []);

  const refreshVersions = useCallback(async (skiAreaId) => {
    try {
      const data = await fetchVersions(skiAreaId);
      setVersions(data.versions);
      setLatest(data.latest);
    } catch (err) {
      addError(err.message, err.stack, 'Map Import');
    }
  }, [addError]);

  useEffect(() => {
    if (selected) refreshVersions(selected.id);
  }, [selected, refreshVersions]);

  // Only one integration can run anywhere at a time, so the publish button has
  // to know whether someone else is mid-publish before it is offered.
  const openConfirm = useCallback(async () => {
    setStaleBase(null);
    try {
      setLock(await fetchLock());
    } catch {
      setLock(null);
    }
    setConfirming(true);
  }, []);

  const handlePublish = useCallback(async (acknowledgeStaleBase = false) => {
    if (!result?.runId || !notes.trim()) return;
    setPublishing(true);
    setError(null);
    try {
      const out = await integrateImport(result.runId, notes.trim(), acknowledgeStaleBase);
      setPublished(out);
      setConfirming(false);
      setStaleBase(null);
      setNotes('');
      refreshVersions(selected.id);
    } catch (err) {
      const payload = err.payload || {};
      if (payload.error === 'stale_base') {
        setStaleBase(payload);
      } else if (payload.error === 'request_failed' || err.message) {
        setError(err.message);
      }
      if (payload.error !== 'stale_base') addError(err.message, err.stack, 'Map Import');
    } finally {
      setPublishing(false);
    }
  }, [result, notes, selected, refreshVersions, addError]);

  const handlePromote = useCallback(async (versionId) => {
    try {
      await promoteVersion(versionId);
      refreshVersions(selected?.id);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Map Import');
    }
  }, [selected, refreshVersions, addError]);

  const report = result?.report;
  const blocked = report && report.status !== 'pass';
  const rows = result?.features?.[tab] || [];

  return (
    <div className="map-import-page">
      <div className="map-import-header">
        <button className="map-import-back" onClick={() => navigate('/map-management')}>
          &larr; Map Management
        </button>
        <h2>Manual Labeling Map Import</h2>
      </div>

      <div className="map-import-setup">
        <div className="map-import-field">
          <label htmlFor="mi-resort">Ski area</label>
          {selected ? (
            <div className="map-import-selected">
              <span>
                <strong>{selected.name}</strong>
                <span className="map-import-muted">
                  {' '}{selected.region || ''} {selected.country || ''} &middot; {selected.id}
                </span>
              </span>
              <Button variant="outline" size="small" onClick={() => { setSelected(null); handleReset(); }}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <input
                id="mi-resort"
                type="text"
                placeholder={loadingAreas ? 'Loading ski areas…' : 'Search by name…'}
                value={query}
                disabled={loadingAreas}
                onChange={(e) => setQuery(e.target.value)}
              />
              {matches.length > 0 && (
                <ul className="map-import-results">
                  {matches.map(area => (
                    <li key={area.id}>
                      <button onClick={() => { setSelected(area); setQuery(''); }}>
                        <strong>{area.name}</strong>
                        <span className="map-import-muted">
                          {' '}{area.region || ''} {area.country || ''} &middot; {area.id}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="map-import-field">
          <label htmlFor="mi-url">Mapbox dataset link</label>
          <input
            id="mi-url"
            type="text"
            placeholder="https://studio.mapbox.com/datasets/…"
            value={mapboxUrl}
            onChange={(e) => setMapboxUrl(e.target.value)}
          />
        </div>

        <Button
          variant="primary"
          onClick={handlePull}
          disabled={!selected || !mapboxUrl.trim() || busy}
        >
          {busy ? 'Pulling and sanitizing…' : 'Pull and sanitize'}
        </Button>
      </div>

      {error && <div className="map-import-error">{error}</div>}

      {result && (
        <div className="map-import-result">
          <div className="map-import-summary">
            <span className="map-import-muted">
              Pulled {result.mapbox.featuresPulled} features over {result.mapbox.pages}{' '}
              page{result.mapbox.pages === 1 ? '' : 's'} from dataset {result.mapbox.datasetId}
            </span>
            <div className="map-import-swap">
              <strong>{result.swap.after.runs}</strong> runs,{' '}
              <strong>{result.swap.after.lifts}</strong> lifts,{' '}
              <strong>{result.swap.after.pois}</strong> POIs would replace{' '}
              {result.swap.before.runs} runs, {result.swap.before.lifts} lifts,{' '}
              {result.swap.before.pois} POIs
            </div>
          </div>

          {blocked ? (
            <div className="map-import-blocking">
              <h3>{report.blocking.length} blocking error{report.blocking.length === 1 ? '' : 's'}</h3>
              <p className="map-import-muted">
                Fix these in Mapbox, then pull again. Nothing can be imported until the list is empty.
              </p>
              <ul>
                {report.blocking.map((b, i) => (
                  <li key={`${b.code}-${i}`}>
                    <code>{b.code}</code> {b.message}
                  </li>
                ))}
              </ul>
              <Button variant="primary" size="small" onClick={handlePull} disabled={busy}>
                Re-pull and re-check
              </Button>
            </div>
          ) : (
            <div className="map-import-ok">Sanitization passed.</div>
          )}

          {report.warnings.length > 0 && (
            <div className="map-import-warnings">
              <h3>{report.warnings.length} warning{report.warnings.length === 1 ? '' : 's'}</h3>
              <ul>
                {report.warnings.map((w, i) => (
                  <li key={`${w.code}-${i}`}><code>{w.code}</code> {w.message}</li>
                ))}
              </ul>
            </div>
          )}

          {report.info.length > 0 && (
            <ul className="map-import-info">
              {report.info.map((n, i) => <li key={`${n.code}-${i}`}>{n.message}</li>)}
            </ul>
          )}

          {!blocked && result.runId && <MapImportPreview runId={result.runId} />}

          {result.idChanges && !blocked && (
            <div className="map-import-ids">
              <h3>ID changes</h3>
              <p className="map-import-muted">
                Live status is keyed by feature id, so an id that changes takes that
                lift or trail off the live map until the upstream feed catches up.
              </p>
              <div className="map-import-id-groups">
                <div className="map-import-id-group">
                  <span className="map-import-id-count">{result.idChanges.carried}</span>
                  <span>carried over</span>
                </div>
                <div className={`map-import-id-group${result.idChanges.minted.length ? ' warn' : ''}`}>
                  <span className="map-import-id-count">{result.idChanges.minted.length}</span>
                  <span>newly minted</span>
                </div>
                <div className={`map-import-id-group${result.idChanges.removed.length ? ' danger' : ''}`}>
                  <span className="map-import-id-count">{result.idChanges.removed.length}</span>
                  <span>removed</span>
                </div>
                <div className={`map-import-id-group${result.idChanges.nearMisses.length ? ' warn' : ''}`}>
                  <span className="map-import-id-count">{result.idChanges.nearMisses.length}</span>
                  <span>near-miss</span>
                </div>
                <div className={`map-import-id-group${(result.idChanges.changed || []).length ? ' warn' : ''}`}>
                  <span className="map-import-id-count">{(result.idChanges.changed || []).length}</span>
                  <span>attribute changed</span>
                </div>
              </div>

              {result.idChanges.minted.length > 0 && (
                <details open={result.idChanges.minted.length <= 10}>
                  <summary>
                    Newly minted — no existing feature matched
                    {result.swap.before.runs > 0 && result.idChanges.minted.length > result.idChanges.carried
                      && ' (a large count on a resort that already had data usually means the match failed, not that the annotation is new)'}
                  </summary>
                  <ul>
                    {result.idChanges.minted.map(m => (
                      <li key={m.id}><code>{m.id}</code> {m.kind} &middot; {m.name}</li>
                    ))}
                  </ul>
                </details>
              )}

              {result.idChanges.removed.length > 0 && (
                <details open>
                  <summary>
                    Removed — present today, absent from the annotation. These disappear on publish.
                  </summary>
                  <ul>
                    {result.idChanges.removed.map(m => (
                      <li key={m.id}><code>{m.id}</code> {m.kind} &middot; {m.name}</li>
                    ))}
                  </ul>
                </details>
              )}

              {(result.idChanges.changed || []).length > 0 && (
                <details open>
                  <summary>
                    Attribute changed — kept its id, but its metadata moved.
                    A difficulty or lift-type correction looks identical on the map.
                  </summary>
                  <ul>
                    {result.idChanges.changed.map(c => (
                      <li key={c.id}>
                        <code>{c.id}</code> {c.kind} &middot; {c.name}
                        {Object.entries(c.changes).map(([field, v]) => (
                          <span key={field} className="map-import-change">
                            {field}: <s>{String(v.from ?? '—')}</s> &rarr;{' '}
                            <strong>{String(v.to ?? '—')}</strong>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {result.idChanges.nearMisses.length > 0 && (
                <details>
                  <summary>Near-miss — matched, but check these</summary>
                  <ul>
                    {result.idChanges.nearMisses.map(m => (
                      <li key={m.id}><code>{m.id}</code> {m.kind} &middot; {m.name} — {m.note}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {result.features && (
            <div className="map-import-table-wrap">
              <div className="map-import-tabs">
                {TABS.map(t => (
                  <button
                    key={t.id}
                    className={tab === t.id ? 'active' : ''}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label} ({result.features[t.id].length})
                  </button>
                ))}
              </div>
              <table className="map-import-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>{tab === 'runs' ? 'Color' : tab === 'lifts' ? 'Type' : 'POI type'}</th>
                    {tab === 'lifts' && <th>Occupancy</th>}
                    {tab === 'runs' && <th>Convention</th>}
                    <th>ID</th>
                    <th>Points</th>
                    {tab !== 'pois' && <th>Elevation</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f, i) => {
                    const p = f.properties;
                    const points = f.geometry?.coordinates
                      ? (tab === 'pois' ? 1 : f.geometry.coordinates.length)
                      : 0;
                    return (
                      <tr key={p.id || i} className={p.changed ? 'map-import-row-changed' : ''}>
                        <td>
                          {p.name}
                          {p.changed?.name && (
                            <span className="map-import-change">
                              was <s>{String(p.changed.name.from ?? '—')}</s>
                            </span>
                          )}
                        </td>
                        <td>
                          {p.colorName || p.liftType || p.type}
                          {(p.changed?.colorName || p.changed?.liftType || p.changed?.type) && (
                            <span className="map-import-change">
                              was{' '}
                              <s>
                                {String((p.changed.colorName || p.changed.liftType
                                  || p.changed.type).from ?? '—')}
                              </s>
                            </span>
                          )}
                        </td>
                        {tab === 'lifts' && <td>{p.occupancy ?? '—'}</td>}
                        {tab === 'runs' && <td>{p.convention || '—'}</td>}
                        <td className="map-import-mono">
                          {p.id}
                          {p.idSource === 'new' && <span className="map-import-new-id">new</span>}
                        </td>
                        <td>{points}</td>
                        {tab !== 'pois' && <td className="map-import-pending">pending</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!blocked && !published && (
            <div className="map-import-publish">
              <Button variant="primary" onClick={openConfirm} disabled={publishing}>
                Publish
              </Button>
              <span className="map-import-muted">
                Replaces this resort&apos;s runs, lifts and POIs and makes the result live.
              </span>
            </div>
          )}

          {published && (
            <div className="map-import-ok">
              Published <strong>{published.versionId}</strong> (v{published.versionNumber}).
              It is now live; other backend instances pick it up within a minute.
            </div>
          )}
        </div>
      )}

      {confirming && (
        <div className="map-import-modal-backdrop" onClick={() => !publishing && setConfirming(false)}>
          <div className="map-import-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Publish this import?</h3>

            {lock ? (
              <p className="map-import-lock">
                {lock.holderEmail || 'Another operator'} has been integrating{' '}
                {lock.skiAreaId} since {new Date(lock.acquiredAt).toLocaleTimeString()}.
                Only one integration runs at a time — try again shortly.
              </p>
            ) : (
              <p className="map-import-destructive">
                This deletes all {result.swap.before.runs} runs,{' '}
                {result.swap.before.lifts} lifts and {result.swap.before.pois} POIs for{' '}
                <strong>{result.skiArea.name}</strong> and replaces them with{' '}
                {result.swap.after.runs} runs, {result.swap.after.lifts} lifts and{' '}
                {result.swap.after.pois} POIs from the annotation.
              </p>
            )}

            {staleBase && (
              <p className="map-import-destructive">
                {staleBase.message}
              </p>
            )}

            <label htmlFor="mi-notes">Notes (required)</label>
            <textarea
              id="mi-notes"
              rows={3}
              value={notes}
              placeholder="What changed and why"
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="map-import-modal-actions">
              <Button variant="outline" size="small"
                onClick={() => setConfirming(false)} disabled={publishing}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => handlePublish(Boolean(staleBase))}
                disabled={publishing || !notes.trim() || Boolean(lock)}
              >
                {publishing ? 'Publishing…'
                  : staleBase ? 'Publish over it' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selected && versions && versions.length > 0 && (
        <div className="map-import-history">
          <h3>Version history — {selected.name}</h3>
          <table className="map-import-table">
            <thead>
              <tr>
                <th>Version</th><th>Notes</th><th>By</th><th>When</th><th>Change</th><th />
              </tr>
            </thead>
            <tbody>
              {versions.map(v => {
                const live = latest && latest.versionId === v.versionId;
                return (
                  <tr key={v.versionId}>
                    <td>
                      v{v.versionNumber}{' '}
                      {live && <span className="map-import-live">live</span>}
                    </td>
                    <td>{v.message}</td>
                    <td>{v.publishedByEmail || '—'}</td>
                    <td>{v.publishedAt ? new Date(v.publishedAt).toLocaleString() : '—'}</td>
                    <td>
                      {v.featureDelta
                        ? `${v.featureDelta.after.runs}r / ${v.featureDelta.after.lifts}l / ${v.featureDelta.after.pois}p`
                        : '—'}
                    </td>
                    <td>
                      {!live && (
                        <Button variant="outline" size="small"
                          onClick={() => handlePromote(v.versionId)}>
                          Make live
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MapImportPage;
